import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Input, Space, Row, Col, Statistic,
    Typography, Tabs, Avatar, Progress, DatePicker, Dropdown, message, Select
} from 'antd';
import {
    SearchOutlined, ClockCircleOutlined as ClockIcon, EyeOutlined,
    TeamOutlined, CrownOutlined, WalletOutlined, ReloadOutlined,
    CheckCircleOutlined, ClockCircleOutlined, CarOutlined,
    DownloadOutlined, FileExcelOutlined, CalendarOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';
import XLSX from 'xlsx-js-style';

dayjs.locale('fr');

const { RangePicker } = DatePicker;

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Revenues130hPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [revenues, setRevenues] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [cityFilter, setCityFilter] = useState(null);
    const [defaultHoursThreshold, setDefaultHoursThreshold] = useState(130);
    const [stats, setStats] = useState({
        totalAffiliates: 0,
        totalAmbassadors: 0,
        totalDriversReached130h: 0,
        totalPotentialAmount: 0,
        totalPaidAmount: 0,
        totalPendingAmount: 0
    });

    const fetchRevenues = useCallback(async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.get130hBeneficiaries({
                month: selectedMonth.format('YYYY-MM')
            });

            if (response.success) {
                setRevenues(response.data || []);
                if (response.defaultHoursThreshold) {
                    setDefaultHoursThreshold(response.defaultHoursThreshold);
                }
                if (response.stats) {
                    setStats({
                        totalAffiliates: response.stats.totalAffiliates || 0,
                        totalAmbassadors: response.stats.totalAmbassadors || 0,
                        totalDriversReached130h: response.stats.totalDriversReached130h || 0,
                        totalPotentialAmount: response.stats.totalPotentialAmount || 0,
                        totalPaidAmount: 0, // À calculer si besoin
                        totalPendingAmount: response.stats.totalPotentialAmount || 0
                    });
                }
            }
        } catch (error) {
            console.error('Erreur chargement revenus 130h:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth]);

    // Snapshot + rechargement des données
    const handleActualiser = useCallback(async () => {
        setLoading(true);
        try {
            const period = selectedMonth.format('YYYY-MM');
            await affiliatesService.snapshot130hHours(period);
            message.success('Heures mises à jour avec succès');
            await fetchRevenues();
        } catch (error) {
            console.error('Erreur snapshot 130h:', error);
            message.error('Erreur lors de la mise à jour des heures');
            setLoading(false);
        }
    }, [selectedMonth, fetchRevenues]);

    useEffect(() => {
        fetchRevenues();
    }, [fetchRevenues]);

    const handleViewDetails = (record) => {
        navigate(`/dashboard/revenues-130h/${record.recipientType}/${record._id}?month=${selectedMonth.format('YYYY-MM')}`);
    };

    const handleMonthChange = (date) => {
        if (date) {
            setSelectedMonth(date);
        }
    };

    // Extraire les options de ville depuis les données
    const cityOptions = React.useMemo(() => {
        const cities = {};
        revenues.forEach(r => {
            if (r.city?.name) {
                cities[r.city.name] = r.city;
            }
        });
        return Object.values(cities).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [revenues]);

    const filteredRevenues = revenues.filter(r => {
        if (cityFilter && r.city?.name !== cityFilter) return false;
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        const name = `${r.firstName || ''} ${r.lastName || ''}`.toLowerCase();
        const phone = (r.phone || '').toLowerCase();
        return name.includes(search) || phone.includes(search);
    });

    const getTypeTag = (type) => {
        if (type === 'affiliate') {
            return <Tag color="blue" icon={<TeamOutlined />}>Affilié</Tag>;
        }
        return <Tag color="purple" icon={<CrownOutlined />}>Ambassadeur</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        const dataToExport = filteredRevenues.filter(r => activeTab === 'all' || r.recipientType === activeTab);
        return dataToExport.map(r => {
            const threshold = r.hoursThreshold || defaultHoursThreshold;
            return {
                'Nom': `${r.firstName || ''} ${r.lastName || ''}`.trim(),
                'Téléphone': r.phone || '',
                'Ville': r.city?.name || '-',
                'Type': r.recipientType === 'affiliate' ? 'Affilié' : 'Ambassadeur',
                'Chauffeurs': r.driversCount || 0,
                'Heures totales': r.totalHoursThisMonth || 0,
                [`${threshold}h atteint`]: r.driversReached130h || 0,
                [`Revenus ${threshold}h (F)`]: r.potentialAmount || 0,
                [`Commission/${threshold}h (F)`]: r.commissionAmount || 0,
                'Progression (%)': r.driversCount > 0 ? Math.round((r.driversReached130h || 0) / r.driversCount * 100) : 0
            };
        });
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            let titleLine = `REVENUS ${defaultHoursThreshold}H MENSUEL`;
            if (activeTab === 'affiliate') titleLine = `REVENUS ${defaultHoursThreshold}H DES AFFILIÉS`;
            else if (activeTab === 'ambassador') titleLine = `REVENUS ${defaultHoursThreshold}H DES AMBASSADEURS`;

            const periodLine = `PÉRIODE: ${selectedMonth.format('MMMM YYYY').toUpperCase()}`;

            const headers = Object.keys(data[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...data.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `revenus_${defaultHoursThreshold}h_${selectedMonth.format('YYYY-MM')}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            // Styles
            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "FA8C16" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            let titleText = `REVENUS ${defaultHoursThreshold}H MENSUEL`;
            if (activeTab === 'affiliate') titleText = `REVENUS ${defaultHoursThreshold}H DES AFFILIÉS`;
            else if (activeTab === 'ambassador') titleText = `REVENUS ${defaultHoursThreshold}H DES AMBASSADEURS`;

            const periodText = `PÉRIODE: ${selectedMonth.format('MMMM YYYY').toUpperCase()}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(data[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...data.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, `Revenus ${defaultHoursThreshold}h`);

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const filename = `revenus_${defaultHoursThreshold}h_${selectedMonth.format('YYYY-MM')}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            width: 280,
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={45}
                        icon={record.recipientType === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                        style={{
                            backgroundColor: record.recipientType === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                    />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>{record.phone}</div>
                        {getTypeTag(record.recipientType)}
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            width: 120,
            render: (_, record) => (
                record.city?.name ? (
                    <Tag icon={<EnvironmentOutlined />} color="blue">{record.city.name}</Tag>
                ) : (
                    <Tag color="default">-</Tag>
                )
            )
        },
        {
            title: 'Chauffeurs',
            dataIndex: 'driversCount',
            key: 'driversCount',
            width: 120,
            align: 'center',
            render: (count) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                        {count || 0}
                    </div>
                    <div style={{ fontSize: 11, color: '#888' }}>chauffeurs</div>
                </div>
            )
        },
        {
            title: 'Heures totales',
            dataIndex: 'totalHoursThisMonth',
            key: 'totalHours',
            width: 130,
            align: 'center',
            render: (hours) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 'bold', color: '#fa8c16' }}>
                        {hours || 0}h
                    </div>
                    <div style={{ fontSize: 11, color: '#888' }}>ce mois</div>
                </div>
            )
        },
        {
            title: 'Seuil atteint',
            key: 'reached',
            width: 140,
            align: 'center',
            render: (_, record) => {
                const threshold = record.hoursThreshold || defaultHoursThreshold;
                const count = record.driversReached130h || 0;
                return (
                    <div style={{ textAlign: 'center' }}>
                        {count > 0 ? (
                            <>
                                <Tag color="green" style={{ fontSize: 14, padding: '2px 12px' }}>
                                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                                    {count}
                                </Tag>
                                <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
                                    à {threshold}h
                                </div>
                            </>
                        ) : (
                            <Tag color="default">
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                0 / {threshold}h
                            </Tag>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Revenus',
            key: 'revenue',
            width: 150,
            sorter: (a, b) => (a.potentialAmount || 0) - (b.potentialAmount || 0),
            render: (_, record) => {
                const threshold = record.hoursThreshold || defaultHoursThreshold;
                const amount = record.potentialAmount || 0;
                return (
                    <div>
                        <div style={{
                            fontSize: 18,
                            fontWeight: 'bold',
                            color: amount > 0 ? '#52c41a' : '#bfbfbf'
                        }}>
                            {amount.toLocaleString()} F
                        </div>
                        <div style={{ fontSize: 11, color: '#888' }}>
                            {(record.commissionAmount || 0).toLocaleString()} F / {threshold}h
                        </div>
                        {record.commissions130hCount > 0 && (
                            <div style={{ fontSize: 11, color: '#888' }}>
                                {record.commissions130hCount} commission{record.commissions130hCount > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Progression',
            key: 'progress',
            width: 150,
            render: (_, record) => {
                const totalDrivers = record.driversCount || 0;
                const reached = record.driversReached130h || 0;
                const threshold = record.hoursThreshold || defaultHoursThreshold;
                const percent = totalDrivers > 0 ? Math.round((reached / totalDrivers) * 100) : 0;
                return (
                    <div style={{ width: 120 }}>
                        <Progress
                            percent={percent}
                            size="small"
                            strokeColor={percent === 100 ? '#52c41a' : '#fa8c16'}
                            format={() => `${percent}%`}
                        />
                        <div style={{ fontSize: 10, color: '#888', textAlign: 'center' }}>
                            {reached}/{totalDrivers} à {threshold}h
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    Détails
                </Button>
            )
        }
    ];

    const totalAll = stats.totalAffiliates + stats.totalAmbassadors;

    return (
        <div className="revenues-page">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <ClockIcon style={{ marginRight: 12 }} />
                        Revenus {defaultHoursThreshold}h Mensuel
                    </Title>
                    <Text type="secondary">
                        Vue d'ensemble des revenus {defaultHoursThreshold}h des affiliés et ambassadeurs
                    </Text>
                </div>
                <Space wrap>
                    <DatePicker
                        picker="month"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        format="MMMM YYYY"
                        style={{ borderRadius: 8 }}
                        allowClear={false}
                        locale={locale}
                    />
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleActualiser}
                        loading={loading}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Statistiques globales */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><TeamOutlined style={{ marginRight: 8 }} />Affiliés</span>}
                            value={stats.totalAffiliates}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CrownOutlined style={{ marginRight: 8 }} />Ambassadeurs</span>}
                            value={stats.totalAmbassadors}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><CarOutlined style={{ marginRight: 8, color: '#52c41a' }} />Chauffeurs {defaultHoursThreshold}h</span>}
                            value={stats.totalDriversReached130h}
                            valueStyle={{ color: '#52c41a' }}
                        />
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                            ont atteint {defaultHoursThreshold}h ce mois
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={<span><WalletOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Total potentiel</span>}
                            value={stats.totalPotentialAmount}
                            suffix="F"
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Info mois */}
            <Card
                style={{
                    marginBottom: 16,
                    background: '#fff7e6',
                    border: '1px solid #ffd591'
                }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <ClockIcon style={{ fontSize: 18, color: '#fa8c16' }} />
                    <div>
                        <Text strong style={{ color: '#d46b08' }}>
                            Période: {selectedMonth.format('MMMM YYYY')}
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            Les commissions {defaultHoursThreshold}h sont calculées mensuellement pour les nouveaux chauffeurs qui atteignent {defaultHoursThreshold} heures de conduite.
                            <br />
                            <span style={{ color: '#fa8c16' }}>
                                Valable pendant 3 mois après l'inscription du chauffeur. Le seuil peut varier selon la ville.
                            </span>
                        </Text>
                    </div>
                </div>
            </Card>

            {/* Filtres et recherche */}
            <Card style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Input
                        placeholder="Rechercher par nom ou téléphone..."
                        prefix={<SearchOutlined />}
                        style={{ width: 280 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                    {cityOptions.length >= 1 && (
                        <Select
                            placeholder="Filtrer par ville"
                            style={{ width: 180 }}
                            value={cityFilter}
                            onChange={(value) => setCityFilter(value)}
                            allowClear
                            suffixIcon={<EnvironmentOutlined />}
                        >
                            {cityOptions.map(c => (
                                <Select.Option key={c.name} value={c.name}>{c.name}</Select.Option>
                            ))}
                        </Select>
                    )}
                    {cityFilter && (
                        <Tag color="blue" closable onClose={() => setCityFilter(null)}>
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            {cityFilter}
                        </Tag>
                    )}
                    <RangePicker
                        placeholder={['Date début', 'Date fin']}
                        format="DD/MM/YYYY"
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates)}
                        allowClear
                        style={{ width: 280 }}
                        locale={locale}
                        presets={[
                            { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                            { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                            { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] }
                        ]}
                    />
                    {dateRange && (
                        <Tag color="orange" closable onClose={() => setDateRange(null)}>
                            <CalendarOutlined style={{ marginRight: 4 }} />
                            {dateRange[0]?.format('DD/MM/YYYY')} - {dateRange[1]?.format('DD/MM/YYYY')}
                        </Tag>
                    )}
                </Space>
            </Card>

            {/* Onglets et tableau */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><WalletOutlined /> Tous ({totalAll})</span>}
                        key="all"
                    />
                    <TabPane
                        tab={<span><TeamOutlined style={{ color: '#1890ff' }} /> Affiliés ({stats.totalAffiliates})</span>}
                        key="affiliate"
                    />
                    <TabPane
                        tab={<span><CrownOutlined style={{ color: '#722ed1' }} /> Ambassadeurs ({stats.totalAmbassadors})</span>}
                        key="ambassador"
                    />
                </Tabs>

                <Table
                    columns={columns}
                    dataSource={filteredRevenues.filter(r => activeTab === 'all' || r.recipientType === activeTab)}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `${total} bénéficiaire(s)`,
                        defaultPageSize: 20
                    }}
                    scroll={{ x: 1300 }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default Revenues130hPage;
